import { SemVer } from "semver";
import { v4 as uuidv4 } from 'uuid';
import short from 'short-uuid';
import { IEpDevPVersionInfo } from './types';

// use with // @ts-ignore if getting Type instantiation is excessively deep and possibly infinite.  TS2589
type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`;
type DotNestedKeys<T> = (T extends object ?
    { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<keyof T, symbol>]
    : "") extends infer D ? Extract<D, string> : never;
type DeepPartial<T> = T extends object ? {
      [P in keyof T]?: DeepPartial<T[P]>;
} : T;

class EpDevPUtils {
  // private readonly BaseComponentName = "APDisplayUtils";

  public nameOf<T>(name: DotNestedKeys<DeepPartial<T>>) {
    return name;
  }

  /**
   * Use for types of Record<string, T>.
   * @example
   * type T = {
   *  one: {
   *    two: {
   *      three: Record<string, T>;
   *    }
   *  }
   * }
   * const name = EpDevPUtils.nameOfStringRecord<Record<T>("one.two.three.x");
   */
  public nameOfStringRecord<T>(name: DotNestedKeys<DeepPartial<T>>) {
    const xname: string = this.nameOf(name);
    const names = xname.split('.');
    names.pop();
    return names.join('.');
  }

  public getUUID = (): string => { return uuidv4(); }

  public getShortUUID = (): string => { return short.generate(); }

  public assertNever = (extLogName: string, x: never): never => {
    const funcName = 'assertNever';
    const logName = `${EpDevPUtils.name}.${funcName}()`;
    throw new Error(`${logName}:${extLogName}: unexpected object: ${JSON.stringify(x)}`);
  }

  public convertStringToFilePath(str: string): string {
    return str.replace(/[^0-9a-zA-Z\/\.]+/g, '-');
  }

  public sleep = async(millis = 500) => {
    if(millis > 0) await new Promise(resolve => setTimeout(resolve, millis));
  }

  // public convertMilliseconds_To_Days = (ms: number): number => {
  //   let d, h, m, s: number;
  //   s = Math.floor(ms / 1000);
  //   m = Math.floor(s / 60);
  //   s = s % 60;
  //   h = Math.floor(m / 60);
  //   m = m % 60;
  //   d = Math.floor(h / 24);
  //   h = h % 24;
  //   return d;
  // }
  // public convertDays_To_Milliseconds = (days: number): number => {
  //   return (days * 24 * 3600 * 1000);
  // }
  // public convertMilliseconds = (ms: number): string => {
  //   let d, h, m, s;
  //   s = Math.floor(ms / 1000);
  //   m = Math.floor(s / 60);
  //   s = s % 60;
  //   h = Math.floor(m / 60);
  //   m = m % 60;
  //   d = Math.floor(h / 24);
  //   h = h % 24;
  
  //   const pad = (n: number) => { 
  //     return n < 10 ? '0' + n : n; 
  //   };
  
  //   const result = d + ' days, ' + pad(h) + ' hours, ' + pad(m) + ' mins';
  //   return result;
  // };

  public sortEpDevPVersionInfoList({ epDevPVersionInfoList}: {
    epDevPVersionInfoList: Array<IEpDevPVersionInfo>;
  }): Array<IEpDevPVersionInfo> {
    return epDevPVersionInfoList.sort( (e1, e2 ) => {
      const e1SemVer = new SemVer(e1.version);
      const e2SemVer = new SemVer(e2.version);
      return e2SemVer.compare(e1SemVer);
    });
  }

  public getLatestEpDevPVersionInfo({ epDevPVersionInfoList }:{
    epDevPVersionInfoList: Array<IEpDevPVersionInfo>;
  }): IEpDevPVersionInfo {
    const funcName = 'getLatestEpDevPVersionInfo';
    const logName = `${EpDevPUtils.name}.${funcName}()`;
    if(epDevPVersionInfoList.length === 0) throw new Error(`${logName}: epDevPVersionInfoList.length === 0`);
    if(epDevPVersionInfoList.length === 1) return epDevPVersionInfoList[0];
    return this.sortEpDevPVersionInfoList({ epDevPVersionInfoList })[0];
  }

  public isVersionOneGreaterThanVersionTwo({ versionOne, versionTwo }:{
    versionOne: string;
    versionTwo: string;
  }): boolean {
    const e1SemVer = new SemVer(versionOne);
    const e2SemVer = new SemVer(versionTwo);
    return e2SemVer.compare(e1SemVer) === -1;
  }

  public createDeDupedStringList = (list: Array<string>): Array<string> => {
    const unique = new Map<string, number>();
    const distinct: Array<string> = [];
    for(let i=0; i < list.length; i++) {
      if(!unique.has(list[i])) {
        distinct.push(list[i]);
        unique.set(list[i], 1);
      }
    }
    return distinct;
  }

}

export default new EpDevPUtils();